import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft } from 'lucide-react';
import { AUTH_COLORS } from '@/constants/theme';
import { useAuth } from '../hooks/useAuth';
import { FloatingField } from '@/components/ui';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { forgotPassword, forgotPasswordLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery(MD_BREAKPOINT);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    forgotPassword(email, {
      onSuccess: () => setSent(true),
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setError(msg || t('auth.forgotPasswordError'));
      },
    });
  };

  const inputStyle = {
    backgroundColor: 'var(--auth-page-input-bg)',
    border: '1px solid var(--auth-page-input-border)',
    color: 'var(--auth-page-input-text)',
  };

  const formContent = (
    <>
      <Link
        to="/login"
        className="inline-flex items-center gap-1 text-sm font-bold"
        style={{ color: 'var(--auth-page-accent)' }}
      >
        <ArrowLeft size={16} />
        {t('auth.backToLogin')}
      </Link>

      <div className="text-center">
        <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: 'var(--auth-page-heading)' }}>
          {t('auth.forgotPassword')}
        </h1>
        <p className="mt-2 text-sm" style={{ color: 'var(--auth-page-text)', opacity: 0.6 }}>
          {t('auth.forgotPasswordSubtitle')}
        </p>
      </div>

      {sent ? (
        <div
          className="rounded-xl p-4 text-sm font-semibold text-center"
          style={{
            backgroundColor: '#ecfdf5',
            border: '1px solid var(--auth-page-card-border)',
            color: '#065f46',
          }}
        >
          {t('auth.forgotPasswordSuccess')}
        </div>
      ) : (
        <>
          {error && (
            <div
              className="rounded-xl p-3 text-sm font-bold"
              style={{
                backgroundColor: AUTH_COLORS.errorBg,
                border: '1px solid var(--auth-page-card-border)',
                color: AUTH_COLORS.errorText,
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <FloatingField
              inputId="email"
              label={t('auth.email')}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputClassName="focus:ring-2 focus:ring-primary-500"
              inputStyle={inputStyle}
              inputDir="ltr"
            />

            <button
              type="submit"
              disabled={forgotPasswordLoading}
              className="w-full rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'var(--auth-page-button-bg)',
                color: 'var(--auth-page-button-text)',
                border: '1px solid var(--auth-page-card-border)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              {forgotPasswordLoading ? t('common.loading') : t('auth.sendResetLink')}
            </button>
          </form>
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <div className="login-mesh-bg fixed inset-0" style={{ position: 'fixed' }}>
        <div
          className="relative z-10 h-full overflow-y-auto flex flex-col justify-center px-5 py-8 space-y-6"
          style={{
            backgroundColor: 'var(--auth-page-bg)',
            backdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div className="login-mesh-bg fixed inset-0 flex min-h-dvh items-center justify-center p-4" style={{ position: 'fixed' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="space-y-6 rounded-3xl p-8" style={{ backgroundColor: 'var(--auth-page-card-bg)', border: '1px solid var(--auth-page-card-border)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
          {formContent}
        </div>
      </motion.div>
    </div>
  );
}
